import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { toast } from 'react-toastify';
import validator from 'validator';
import { useAuth } from '../../providers/auth_provider';
import { useNavigate } from 'react-router-dom';

export default function UpdateAccount() {
    const { token, adminData, fetchAdminData } = useAuth();
    const [formData, setFormData] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null); // For image preview
    const navigate = useNavigate();

    useEffect(() => {
        setFormData({
            first_name: adminData?.first_name,
            last_name: adminData?.last_name,
            phone_no: adminData?.phone_no,
            status: adminData?.status,
            email: adminData?.email,
            profile_img: adminData?.profile_img // Add profile_img
        });
        setSelectedImage(null); // Clear selectedImage on component mount
    }, [adminData]);

    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (validator.isEmpty(formData.first_name)) {
            newErrors.first_name = 'First name is required';
        }
        if (validator.isEmpty(formData.last_name)) {
            newErrors.last_name = 'Last name is required';
        }
        if (!validator.isMobilePhone(formData.phone_no) && !validator.isEmpty(formData.phone_no)) {
            newErrors.phone_no = 'Invalid phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(URL.createObjectURL(file)); // Show preview of the selected image immediately
            setFormData({ ...formData, profile_img: file }); // Store the file in formData
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        const formDataToSubmit = new FormData(); // Use FormData for file uploads
        Object.keys(formData).forEach((key) => {
            formDataToSubmit.append(key, formData[key]);
        });

        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/admin/update`, formDataToSubmit, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success(response.data.message);
            await fetchAdminData();
            navigate('/profile');
        } catch (error) {
            toast.error(error.response.data.message || 'An error occurred');
        }
    };


    const handleReset = () => {
        // Reset form data to adminData
        setFormData({
            first_name: adminData?.first_name,
            last_name: adminData?.last_name,
            phone_no: adminData?.phone_no,
            status: adminData?.status,
            email: adminData?.email,
            profile_img: adminData?.profile_img // Reset to the original profile image
        });
        setSelectedImage(null); // Reset the selectedImage to null to show the original image
    };

    return (
        <>
            <Helmet>
                <title>Update Profile - Enervybe</title>
            </Helmet>
            <div className="row">
                <div className="col-md-12">
                    <div className="card mb-6">
                        <div className="card-body">
                            <div className="d-flex align-items-start align-items-sm-center gap-6">
                                <img
                                    src={selectedImage || formData?.profile_img || '/assets/img/avatars/1.png'} // Prioritize showing the selected image
                                    alt="user-avatar"
                                    className="d-block w-px-100 rounded-4"
                                    id="uploadedAvatar"
                                />
                                <div className="button-wrapper">
                                    <label htmlFor="upload" className="btn btn-label-primary me-3 mb-4" tabIndex={0}>
                                        <span className="d-none d-sm-block">Upload new photo</span>
                                        <i className="ri-upload-2-line d-block d-sm-none" />
                                        <input
                                            type="file"
                                            id="upload"
                                            className="account-file-input"
                                            hidden
                                            accept="image/png, image/jpeg"
                                            onChange={handleImageChange} // Handle image selection
                                        />
                                    </label>
                                    <div>Allowed JPG, GIF, or PNG. Max size of 800K</div>
                                </div>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <form id="formAccountSettings" onSubmit={handleSubmit}>
                                <div className="row mt-1 g-5">
                                    <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="firstName"
                                                name="first_name"
                                                value={formData?.first_name}
                                                onChange={handleChange}
                                                placeholder="First Name"
                                                autoFocus
                                                required
                                            />
                                            <label htmlFor="firstName">First Name</label>
                                            {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                                                type="text"
                                                id="lastName"
                                                name="last_name"
                                                value={formData?.last_name}
                                                onChange={handleChange}
                                                placeholder="Last Name"
                                                required

                                            />
                                            <label htmlFor="lastName">Last Name</label>
                                            {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                className="form-control"
                                                type="text"
                                                id="email"
                                                name="email"
                                                value={formData?.email}
                                                onChange={handleChange}
                                                placeholder="Email"
                                                required

                                            />
                                            <label htmlFor="email">Email</label>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="input-group input-group-merge">
                                            <div className="form-floating form-floating-outline">
                                                <input
                                                    className={`form-control ${errors.phone_no ? 'is-invalid' : ''}`}
                                                    type="text"
                                                    id="phoneNumber"
                                                    name="phone_no"
                                                    value={formData?.phone_no}
                                                    onChange={handleChange}
                                                    placeholder="Phone Number"
                                                    required

                                                />
                                                <label htmlFor="phoneNumber">Phone Number</label>
                                                {errors.phone_no && <div className="invalid-feedback">{errors.phone_no}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <button type="submit" className="btn btn-success me-3">
                                        Save changes
                                    </button>
                                    <button type="reset" className="btn btn-outline-secondary" onClick={handleReset}>
                                        Reset
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
